/**
 * Storefront Page Config Detail Page with full CRUD operations
 * 
 * Features:
 * - Experience view with page builder widget
 * - Create, Read, Update, Delete operations
 * - Publish functionality
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageContainer } from "@/components/layout/PageContainer";
import { DetailView } from "@/components/layout/DetailView";
import { HeaderActions } from "@/features/inventory/item-detail/components/HeaderActions";
import { useStorefrontPageConfigDetail } from "./api/storefrontPageConfigHooks";
import { useCreateStorefrontPageConfig, useSaveStorefrontPageConfig, useDeleteStorefrontPageConfig } from "./api/storefrontPageConfigHooks";
import { storefrontPageConfigQueryKeys } from "./api/queryKeys";
import { erp } from "@/lib/erp";
import { useAuthStore } from "@/lib/store/authStore";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { prepareDocForSave } from "@/lib/erp/save";
import { resolveView } from "@/lib/views/registry";
import { SystemView } from "@/components/view/SystemView";
import { ExperienceView } from "@/components/view/ExperienceView";
// FEATURE ON HOLD: QuickActions component temporarily removed from UI
// See QuickActions.tsx for implementation details
// import { QuickActions } from "@/components/view/QuickActions";
import type {} from "@/lib/erp/meta";
import { isDirty } from "@/features/inventory/item-detail/utils/computeDirtyState";
import { Button } from "@/components/ui/button";
import { useCanQuery } from "@/hooks/useCanQuery";
import { useGridaEditorHandoff } from "./hooks/useGridaEditorHandoff";
import { DetailPageSkeleton } from "@/features/doc/DetailPageSkeleton";

export function StorefrontPageConfigDetailPage() {
  const params = useParams<{ name?: string }>();
  const navigate = useNavigate();
  const pageConfigName = params.name || "";
  // NOTE: `/storefront/page-config/new` is a dedicated route (no `:name` param),
  // so `params.name` is undefined there. Treat missing param as create mode.
  const isNew = params.name === undefined || pageConfigName === "new";

  const queryClient = useQueryClient();
  
  // Get auth state
  const authStore = useAuthStore();
  const { isAuthenticated } = authStore;

  // Fetch page config detail (if not new)
  const { data: pageConfigData, isLoading: isLoadingPageConfig, error: pageConfigError } = useStorefrontPageConfigDetail(
    isNew ? "" : pageConfigName
  );

  // Fetch meta schema - only when authenticated
  // In control_plane mode, frappeClient is null (we use JWT instead), so just check isAuthenticated
  const { data: metaSchema, isLoading: isLoadingMeta } = useQuery({
    queryKey: ["meta", "Storefront Page Config"],
    queryFn: () => erp.meta.getDocTypeSchema("Storefront Page Config"),
    enabled: isAuthenticated,
  });

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  
  // Refs to track hydration state and prevent snap-back from query refetches
  const hasHydratedRef = useRef(false);
  const currentDocRef = useRef<string>("");
  const isSavingRef = useRef(false);

  // Initialize form data when page config loads
  useEffect(() => {
    if (currentDocRef.current !== pageConfigName) {
      currentDocRef.current = pageConfigName;
      hasHydratedRef.current = false;
    }

    if (pageConfigData) {
      if (!hasHydratedRef.current) {
        setFormData(pageConfigData);
        setOriginalData(pageConfigData);
        hasHydratedRef.current = true;
        return;
      }

      if (isSavingRef.current) {
        return;
      }

      const isDirtyNow = isDirty(originalData, formData);
      if (isDirtyNow) {
        return;
      }
    } else if (isNew && metaSchema) {
      // Initialize defaults once for create route (avoid infinite setState loop).
      if (hasHydratedRef.current) {
        return;
      }
      const defaults: Record<string, any> = {
        sections_json: "[]",
      };
      for (const field of metaSchema.fields) {
        if (field.default !== undefined) {
          defaults[field.fieldname] = field.default;
        }
      }
      setFormData(defaults);
      setOriginalData(defaults);
      hasHydratedRef.current = true;
    }
  }, [pageConfigData, isNew, metaSchema, pageConfigName, originalData, formData]);

  // View resolution
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const devMode = usePreferencesStore((state) => state.devMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);

  const systemMode = devMode ? viewMode === 'system' : false;

  const resolvedView = useMemo(() => {
    if (!metaSchema) return null;
    
    const preferredStyle = !systemMode && experienceStyle !== 'auto' 
      ? experienceStyle 
      : undefined;
    
    return resolveView(
      metaSchema.name,
      undefined,
      systemMode,
      devMode,
      preferredStyle
    );
  }, [metaSchema, systemMode, devMode, experienceStyle]);

  // Permission checks
  const { allowed: canWrite, isLoading: isLoadingWrite } = useCanQuery(
    "Storefront Page Config",
    "write",
    isNew ? undefined : pageConfigName
  );
  const { allowed: canDelete, isLoading: _isLoadingDelete } = useCanQuery(
    "Storefront Page Config",
    "delete",
    isNew ? undefined : pageConfigName
  );

  // Mutations
  const saveMutation = useSaveStorefrontPageConfig(isNew ? "" : pageConfigName);
  const createMutation = useCreateStorefrontPageConfig();
  const deleteMutation = useDeleteStorefrontPageConfig(isNew ? "" : pageConfigName);
  const gridaHandoffMutation = useGridaEditorHandoff();

  // Compute dirty state
  const dirtyState = useMemo(() => {
    if (isNew) {
      return Object.values(formData).some((val) => val !== null && val !== undefined && val !== "");
    }
    return isDirty(originalData, formData);
  }, [formData, originalData, isNew]);

  // Handle field change
  const handleFieldChange = (fieldname: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
  };

  // Handle save
  const handleSave = async (dataOverride?: Record<string, any>) => {
    if (!metaSchema) {
      alert('Cannot save: metadata not loaded');
      return;
    }
    
    // Guard: Ignore React event objects if accidentally passed
    if (dataOverride && ('type' in dataOverride || 'nativeEvent' in dataOverride || '_reactName' in dataOverride)) {
      // This is a React event object, ignore it and use formData instead
      dataOverride = undefined;
    }
    
    const dataToSave = dataOverride ?? formData;
    const docToSave = prepareDocForSave(dataToSave, metaSchema);
    
    isSavingRef.current = true;
    
    try {
      let updatedPageConfig;
    
      if (isNew) {
        updatedPageConfig = await createMutation.mutateAsync(docToSave);
      } else {
        updatedPageConfig = await saveMutation.mutateAsync(docToSave);
      }
      
      setFormData(updatedPageConfig);
      setOriginalData(updatedPageConfig);
      
      queryClient.setQueryData(storefrontPageConfigQueryKeys.detail(pageConfigName), updatedPageConfig);
    } catch (error) {
      console.error('[handleSave] ERROR:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Show to user
      alert(`Failed to save: ${errorMessage}`);
    } finally {
      isSavingRef.current = false;
    }
  };

  // Handle delete
  const handleDelete = () => {
    deleteMutation.mutate();
  };

  // Handle open Grida editor
  const handleOpenGridaEditor = async () => {
    if (!pageConfigName || isNew) {
      return;
    }

    try {
      const result = await gridaHandoffMutation.mutateAsync(pageConfigName);
      if (result.url) {
        // Open Grida editor in new tab with security attributes
        window.open(result.url, "_blank", "noopener,noreferrer");
      } else if (result.error) {
        alert(`Failed to open Grida editor: ${result.error}`);
      }
    } catch (error) {
      console.error("[handleOpenGridaEditor] Error:", error);
      alert("Failed to open Grida editor. Please try again.");
    }
  };

  // Get page title
  const pageTitle = useMemo(() => {
    if (isNew) {
      return "New Page Config";
    }
    
    if (resolvedView?.mode === 'experience' && formData?.page_id) {
      return `Page • ${formData.page_id}`;
    }
    
    return pageConfigData?.name || "Page Config Detail";
  }, [isNew, resolvedView?.mode, formData, pageConfigData]);

  // Loading state — structural skeleton matching the final UI to prevent layout shift
  if (isLoadingPageConfig || isLoadingMeta) {
    return (
      <PageContainer className="max-w-[940px]">
        <DetailPageSkeleton doctype="Storefront Page Config" />
      </PageContainer>
    );
  }

  // Error state
  if (pageConfigError && !isNew) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Error loading page config</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DetailView
        title={pageTitle}
        actions={
          <div className="flex items-center gap-3">
            {/* FEATURE ON HOLD: QuickActions temporarily removed from UI
                To re-enable: uncomment QuickActions import and restore this block
            */}
            {/* {resolvedView?.mode === 'experience' && (
              <QuickActions
                doctype="Storefront Page Config"
                documentName={!isNew ? pageConfigName : undefined}
                formData={formData}
                onActionSuccess={() => {
                  if (!isNew && pageConfigName) {
                    queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.detail(pageConfigName) });
                  }
                }}
              />
            )} */}
            {!isNew && pageConfigName && !isLoadingWrite && canWrite && (
              <>
                <Button
                  variant="outline"
                  onClick={handleOpenGridaEditor}
                  disabled={gridaHandoffMutation.isPending}
                >
                  {gridaHandoffMutation.isPending ? "Opening..." : "Open Grida Editor"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/grida-editor-test?pageConfig=${pageConfigName}`)}
                >
                  Open Grida Test Page
                </Button>
              </>
            )}
            <HeaderActions
              onSave={canWrite ? handleSave : () => {}}
              onPermanentDelete={!isNew && canDelete ? handleDelete : undefined}
              isSaving={saveMutation.isPending || createMutation.isPending}
              isDirty={canWrite && dirtyState}
              isNew={isNew}
              itemName={!isNew ? pageConfigName : undefined}
              doctype="Storefront Page Config"
              deleteLabel="page config"
            />
          </div>
        }
      >
        {metaSchema && resolvedView ? (
          resolvedView.mode === 'system' ? (
            <SystemView
              meta={metaSchema}
              formData={formData}
              onFieldChange={handleFieldChange}
              disabled={!canWrite || saveMutation.isPending || createMutation.isPending}
              onSave={handleSave}
              isSaving={saveMutation.isPending || createMutation.isPending}
              documentName={isNew ? undefined : pageConfigName}
            />
          ) : resolvedView.view ? (
            <ExperienceView
              view={resolvedView.view}
              meta={metaSchema}
              formData={formData}
              onFieldChange={handleFieldChange}
              disabled={!canWrite || saveMutation.isPending || createMutation.isPending}
              onSave={handleSave}
              isSaving={saveMutation.isPending || createMutation.isPending}
              documentName={isNew ? undefined : pageConfigName}
            />
          ) : (
            <div className="text-muted-foreground">No view available</div>
          )
        ) : (
          <div className="text-muted-foreground">Loading view configuration...</div>
        )}
      </DetailView>
    </PageContainer>
  );
}
